import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import { applyRobots } from "@/lib/robots";
import { pingVisit } from "@/lib/membership";
import Onboarding from "@/pages/Onboarding";
import { PushProvider } from "@/context/PushContext";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Article from "@/pages/Article";
import Horoscope from "@/pages/Horoscope";
import MySign from "@/pages/MySign";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import ForYou from "@/pages/ForYou";
import TagView from "@/pages/TagView";
import Coupon from "@/pages/Coupon";
import Vantaggi from "@/pages/Vantaggi";
import Meteo from "@/pages/Meteo";
import Search from "@/pages/Search";
import RadarTopic from "@/pages/RadarTopic";
import Contatti from "@/pages/Contatti";
import Founder from "@/pages/Founder";
import Termini from "@/pages/Termini";
import Rules from "@/pages/Rules";
import Rewards from "@/pages/Rewards";
import Levels from "@/pages/Levels";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import GoogleOAuthCallback from "@/pages/GoogleOAuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ComingSoon, { NotFound } from "@/pages/ComingSoon";
import { AdProvider, GlobalAds, SelectorAds } from "@/components/Ads";

function Gami({ children }) {
  const { gamificationEnabled, loaded } = useSettings();
  if (!loaded) return null;
  return gamificationEnabled ? children : <Navigate to="/profilo" replace />;
}

function AppRouter() {
  const location = useLocation();
  const navType = useNavigationType();
  useEffect(() => applyRobots(location.pathname), [location.pathname]);
  useEffect(() => { if (!location.pathname.startsWith("/admin")) pingVisit(); }, [location.pathname]);
  // Cambio pagina/categoria: sempre in alto (il tasto Indietro mantiene la posizione)
  useEffect(() => { if (navType !== "POP") window.scrollTo(0, 0); }, [location.pathname, navType]);
  // Process Emergent OAuth return synchronously (fragment carries session_id).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/per-te" element={<ForYou />} />
        <Route path="/tag/:slug" element={<TagView />} />
        <Route path="/categoria/:slug" element={<Home />} />
        <Route path="/articolo/:id/:slug" element={<Article />} />
        <Route path="/articolo/:id" element={<Article />} />
        <Route path="/oroscopo" element={<Horoscope />} />
        <Route path="/il-mio-segno" element={<MySign />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registrati" element={<Register />} />
        <Route path="/profilo" element={<Profile />} />
        <Route path="/benvenuto" element={<Onboarding />} />
        <Route path="/contatti" element={<Contatti />} />
        <Route path="/founder" element={<Founder />} />
        <Route path="/termini" element={<Termini />} />
        <Route path="/coupon" element={<Coupon />} />
        <Route path="/vantaggi" element={<Vantaggi />} />
        <Route path="/meteo" element={<Meteo />} />
        <Route path="/cerca" element={<Search />} />
        <Route path="/radar/:slug" element={<RadarTopic />} />
        <Route path="/premi" element={<Gami><Rewards /></Gami>} />
        <Route path="/livelli" element={<Gami><Levels /></Gami>} />
        <Route path="/quiz" element={<Gami><ComingSoon title="Quiz del giorno" /></Gami>} />
        <Route path="/storico-punti" element={<Gami><ComingSoon title="Storico punti" /></Gami>} />
        <Route path="/riscatti" element={<Gami><ComingSoon title="I miei riscatti" /></Gami>} />
        <Route path="/regole" element={<Gami><Rules /></Gami>} />
        <Route path="/missioni" element={<Gami><Rules /></Gami>} />
        <Route path="/admin" element={<Admin />} />
        {/* Permalink parlante articolo: /categoria/postname */}
        <Route path="/:category/:slug" element={<Article />} />
        <Route path="/password-dimenticata" element={<ForgotPassword />} />
        <Route path="/reimposta-password" element={<ResetPassword />} />
        <Route path="/auth/google" element={<GoogleOAuthCallback />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors closeButton theme="dark" />
            <PushProvider>
              <AdProvider>
                <GlobalAds />
                <SelectorAds />
                <AppRouter />
              </AdProvider>
            </PushProvider>
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
